// Plazas: la ciudad que escribe el candidato (libre, viene del bot) agrupada en la plaza donde se contrata.
// «Querétaro, Qro», «San Juan del Río, Qro.» y «Querétaro, Querétaro» son la misma plaza: Querétaro.
//
// PARA AGREGAR UNA PLAZA: suma un renglón a PLAZAS (clave estable, etiqueta, corta y sus frases) y su caso en
// pruebas/unidad/plazas.test.ts. Nada más: el filtro del portal y los conteos salen de aquí.
//
// Cómo se decide (en este orden):
//   1. Por ESTADO: si el texto menciona un estado de alguna plaza («Qro», «N.L.», «Edomex», «Q. Roo»), gana ese.
//      Así «Guadalupe, Zacatecas» NO cae en Monterrey por llamarse Guadalupe… a menos que nadie diga el estado.
//   2. Por CIUDAD o municipio («Apodaca», «San Juan del Río», «Cancún»).
//   Si hay varias, gana la que aparece primero en el texto. Si nada coincide: «otra» (o «sin_dato» si viene vacío).
// Las frases se comparan sin acentos, sin mayúsculas y por palabra completa («nl» no coincide dentro de «online»).

export type ClavePlaza = "monterrey" | "queretaro" | "cdmx" | "cancun" | "otra";

export type Plaza = {
  clave: ClavePlaza;
  /** Nombre completo (título del chip y ayuda). */
  etiqueta: string;
  /** Lo que se lee en el chip (cabe a 400 px). */
  corta: string;
  /** Frases que nombran al estado (paso 1). */
  estados: string[];
  /** Ciudades o municipios (paso 2). */
  ciudades: string[];
};

export const PLAZAS: Plaza[] = [
  {
    clave: "monterrey",
    etiqueta: "Monterrey y área metropolitana",
    corta: "Monterrey",
    estados: ["nuevo leon", "n l", "nl"],
    ciudades: [
      "monterrey",
      "mty",
      "area metropolitana de monterrey",
      "san pedro garza garcia",
      "san pedro",
      "san nicolas de los garza",
      "san nicolas",
      "guadalupe",
      "apodaca",
      "general escobedo",
      "escobedo",
      "santa catarina",
      "garcia",
      "cadereyta",
      "santiago",
      "salinas victoria",
      "pesqueria",
      "cienega de flores",
      "general zuazua",
      "zuazua",
      "el carmen",
    ],
  },
  {
    clave: "queretaro",
    etiqueta: "Querétaro (incluye San Juan del Río)",
    corta: "Querétaro",
    estados: ["queretaro", "qro"],
    ciudades: [
      "santiago de queretaro",
      "san juan del rio",
      "corregidora",
      "el marques",
      "pedro escobedo",
      "tequisquiapan",
      "juriquilla",
    ],
  },
  {
    clave: "cdmx",
    etiqueta: "Ciudad de México y Edomex",
    corta: "CDMX y Edomex",
    estados: ["ciudad de mexico", "cdmx", "cd de mexico", "df", "d f", "distrito federal", "estado de mexico", "edomex", "edo mex", "edo de mexico"],
    ciudades: [
      "toluca",
      "naucalpan",
      "tlalnepantla",
      "ecatepec",
      "nezahualcoyotl",
      "neza",
      "cuautitlan",
      "cuautitlan izcalli",
      "huixquilucan",
      "atizapan",
      "tecamac",
      "texcoco",
      "chalco",
      "coyoacan",
      "iztapalapa",
      "tlalpan",
      "miguel hidalgo",
      "cuauhtemoc",
      "azcapotzalco",
      "gustavo a madero",
      "alvaro obregon",
      "xochimilco",
      "santa fe",
    ],
  },
  {
    clave: "cancun",
    etiqueta: "Cancún",
    corta: "Cancún",
    estados: ["quintana roo", "q roo", "qroo"],
    ciudades: ["cancun", "puerto morelos", "playa del carmen"],
  },
];

/** La plaza que no es ninguna de las de arriba, o que el candidato no dijo. */
export const OTRA: Plaza = { clave: "otra", etiqueta: "Otra ciudad o sin dato", corta: "Otra / sin dato", estados: [], ciudades: [] };

/** Todas, en el orden en que salen los chips. */
export const TODAS_LAS_PLAZAS: Plaza[] = [...PLAZAS, OTRA];

export const esClavePlaza = (v: string | null | undefined): v is ClavePlaza => !!v && TODAS_LAS_PLAZAS.some((p) => p.clave === v);

export const plazaPorClave = (c: ClavePlaza): Plaza => TODAS_LAS_PLAZAS.find((p) => p.clave === c) ?? OTRA;

/** Minúsculas, sin acentos y la puntuación como espacio: «San Juan del Río, Qro.» → «san juan del rio qro». */
export function normalizarCiudad(t: string): string {
  return t
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9ñ]+/g, " ")
    .trim();
}

/** Posición (en palabras completas) de la primera frase de `frases` dentro de `texto`; -1 si ninguna. */
function primera(texto: string, frases: string[]): number {
  const t = ` ${texto} `;
  let mejor = -1;
  for (const f of frases) {
    const i = t.indexOf(` ${f} `);
    if (i >= 0 && (mejor < 0 || i < mejor)) mejor = i;
  }
  return mejor;
}

function ganadora(texto: string, campo: "estados" | "ciudades"): ClavePlaza | null {
  let mejor: { clave: ClavePlaza; i: number } | null = null;
  for (const p of PLAZAS) {
    const i = primera(texto, p[campo]);
    if (i >= 0 && (!mejor || i < mejor.i)) mejor = { clave: p.clave, i };
  }
  return mejor?.clave ?? null;
}

/** La plaza de una ciudad escrita a mano. Vacío o desconocido → «otra». */
export function plazaDe(ciudad: string | null | undefined): ClavePlaza {
  const t = normalizarCiudad(ciudad ?? "");
  if (!t) return "otra";
  return ganadora(t, "estados") ?? ganadora(t, "ciudades") ?? "otra";
}

/** Cuántos hay en cada plaza (todas las claves, aunque sea 0). */
export function contarPorPlaza<T extends { ciudad: string | null }>(items: T[]): Record<ClavePlaza, number> {
  const n = Object.fromEntries(TODAS_LAS_PLAZAS.map((p) => [p.clave, 0])) as Record<ClavePlaza, number>;
  for (const k of items) n[plazaDe(k.ciudad)]++;
  return n;
}
